import React, { useState, useEffect } from "react";
import { fetchDistributors, clearDistributors } from "../../features/distributor/DistributorSlice";
import { useAppDispatch, useAppSelector } from "../../store/hook";
import { RootState } from "../../store/store";
import { Search } from "lucide-react";
import { Loader2, AlertCircle, Plus, Settings } from "lucide-react";
import { fetchPrincipalList, Principal } from "../../features/principal/PrincipalSlice";
import PrincipalTopUpPage from "./PrincipalWallet/PrincipalTopUpPage";
import AddPrincipalPage from "./addPrincipal/AddPrincipalPage";
import EditPrincipalPage from "./addPrincipal/EditPrincipalPage";


const Principals: React.FC = () => {
  const dispatch = useAppDispatch();
  const { loading, error, principalList } = useAppSelector((state: RootState) => state.principal);
  const [selectedPrincipal, setSelectedPrincipal] = useState<Principal | null>(null);
  const [showTopUpForPrincipal, setShowTopUpForPrincipal] = useState(false);
  const [showAddPrincipal, setShowAddPrincipal] = useState(false);
  const [editingPrincipalId, setEditingPrincipalId] = useState<string | null>(null);

  useEffect(() => {
    dispatch(fetchPrincipalList());
  }, [dispatch]);

  const handleAddPrincipal = () => {
    setShowAddPrincipal(true);
  };

  const handleAddPrincipalBack = () => {
    setShowAddPrincipal(false);
    dispatch(fetchPrincipalList());
  };

  const handleEdit = (principalId: string) => {
    setEditingPrincipalId(principalId);
  };

  const handleEditBack = () => {
    setEditingPrincipalId(null);
    dispatch(fetchPrincipalList());
  };

  const handleTopUp = (principal: Principal) => {
    setSelectedPrincipal(principal);
    setShowTopUpForPrincipal(true);
  };

  const handleTopUpBack = async () => {
    setSelectedPrincipal(null);
    setShowTopUpForPrincipal(false);
    dispatch(fetchDistributors(null));
  };

  if (showAddPrincipal) {
    return <AddPrincipalPage onBack={handleAddPrincipalBack} />;
  }

  if (editingPrincipalId) {
    return <EditPrincipalPage principalId={editingPrincipalId} onBack={handleEditBack} />;
  }

  if (selectedPrincipal && showTopUpForPrincipal) {
    return <PrincipalTopUpPage principal={selectedPrincipal} onBack={handleTopUpBack} />;
  }


  const header = (
    <div className="flex items-center gap-3">
      <h1 className="text-2xl font-bold text-gray-900">
        Principal ({principalList?.length || 0})
      </h1>
    </div>
  );

  const headerSidebar = (
    <div className="flex items-center gap-3">
      <button
        onClick={handleAddPrincipal}
        className="px-4 py-2 rounded-md text-sm font-medium text-white bg-brand hover:bg-brand/80 focus:outline-none focus:border-0 focus:ring-1 focus:ring-brand transition-colors flex items-center gap-2"
      >
        <Plus className="w-4 h-4" />
        Add Principal
      </button>
    </div>
  );


  return (
    <div className="bg-white h-full">
      <div className="flex border-b border-cardborder mb-8 flex-col md:flex-row md:items-center md:justify-between max-w-7xl mx-auto px-7 py-8">
        <div className="flex items-center h-10">
          {header}
        </div>
        <div className="flex items-center h-10">
          {headerSidebar}
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-8 sm:px-6 mb-8 lg:px-8">
        {error && (
          <div className="bg-white rounded-xl p-6 flex flex-col justify-center min-h-[120px] transition-shadow duration-200 transition-colors border-[1px] border-cardborder mb-8">
            <div className="flex items-center gap-2 justify-center text-red-500">
              <AlertCircle className="w-5 h-5" />
              <span>{error ? error : "Something went wrong!"}</span>
            </div>
          </div>
        )}

        {loading && (
          <div className="bg-white rounded-xl p-6 flex flex-col justify-center min-h-[120px] transition-shadow duration-200 transition-colors border-[1px] border-cardborder mb-8">
            <div className="flex justify-center items-center gap-2">
              <Loader2 className="w-10 h-10 animate-spin text-brand/80" />
              <span className="text-sm text-gray-500">Loading...</span>
            </div>
          </div>
        )}

        {!loading && !error && principalList && (
          <div className="space-y-4">
            {principalList.map((item) => (
              <div
                key={item.principalId}
                className="bg-white rounded-xl p-6 flex flex-col md:flex-row md:items-center md:justify-between transition-shadow duration-200 transition-colors border-[1px] border-cardborder hover:shadow-md"
              >
                <div className="flex-1 md:mb-0">
                  <div className="text-lg font-bold text-gray-900 mb-1">
                    {item.name}
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <button
                    onClick={() =>
                      handleTopUp(item)
                    }
                    className="px-4 py-2 text-sm font-medium text-brand border border-brand rounded-md hover:bg-brand hover:text-white focus:outline-none transition-colors"
                  >
                    Top Up
                  </button>
                  <button
                    onClick={() => handleEdit(item.principalId)}
                    className="p-2 text-green-600 hover:text-green-700 hover:bg-gray-50 hover:rounded-full rounded-md transition-colors"
                    title="Edit Principal"
                  >
                    <Settings className="w-5 h-5" />
                  </button>
                </div>
              </div>
            ))}

            {principalList.length === 0 && (
              <div className="bg-white rounded-xl p-6 flex flex-col justify-center min-h-[120px] transition-shadow duration-200 transition-colors border-[1px] border-cardborder">
                <div className="flex items-center gap-2 justify-center text-gray-500">
                  <span>No Principals found.</span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Principals;
